import React, { useState } from 'react';
import { db, handleFirestoreError, OperationType } from '../lib/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { CheckCircle2, Loader2, Send } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

type UserType = 'Tutor' | 'Learner' | 'Organisation' | 'Professional';

export function WaitlistForm() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [userType, setUserType] = useState<UserType>('Learner');
  const [intendedUse, setIntendedUse] = useState('');
  const [wantsToTestMvp, setWantsToTestMvp] = useState<'Yes' | 'No'>('Yes');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    const path = 'waitlist_submissions';
    try {
      await addDoc(collection(db, path), {
        name,
        email,
        userType,
        intendedUse,
        wantsToTestMvp,
        submittedAt: serverTimestamp(),
      });
      setSubmitted(true);
    } catch (err) {
      console.error('Waitlist submission error:', err);
      setError('Something went wrong. Please try again.');
      // Special handling for security rules or other errors
      try {
        handleFirestoreError(err, OperationType.CREATE, path);
      } catch (formattedError) {
        // We already set a user-friendly error above
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-8 text-center"
      >
        <div className="w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-emerald-500/20">
          <CheckCircle2 className="w-8 h-8 text-white" />
        </div>
        <h3 className="text-2xl font-bold text-text-main mb-2">Thank you!</h3>
        <p className="text-text-alt">
          Thank you. You are on the Lumina Learn early access list. We will notify you when more testing slots open.
        </p>
      </motion.div>
    );
  }

  return (
    <div className="bg-bg-alt/50 border border-border-main rounded-3xl p-6 sm:p-8 shadow-xl">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="text-sm font-bold text-text-main">Name</label>
            <input
              required
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-3 bg-bg-main border border-border-main rounded-xl focus:ring-2 focus:ring-primary outline-none transition-all"
              placeholder="Your name"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-bold text-text-main">Email</label>
            <input
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 bg-bg-main border border-border-main rounded-xl focus:ring-2 focus:ring-primary outline-none transition-all"
              placeholder="you@example.com"
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-bold text-text-main">User type</label>
          <div className="grid grid-cols-2 sm:grid-cols-2 gap-2">
            {['Tutor', 'Learner', 'Organisation', 'Professional'].map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => setUserType(type as UserType)}
                className={`px-3 py-2 rounded-lg text-sm font-medium border transition-all ${
                  userType === type 
                    ? 'bg-primary text-white border-primary shadow-lg shadow-primary/20' 
                    : 'bg-bg-main text-text-alt border-border-main hover:border-primary/50'
                }`}
              >
                {type}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-bold text-text-main">What would you use Lumina for?</label>
          <textarea
            required
            value={intendedUse}
            onChange={(e) => setIntendedUse(e.target.value)}
            className="w-full px-4 py-3 bg-bg-main border border-border-main rounded-xl focus:ring-2 focus:ring-primary outline-none transition-all min-h-[100px]"
            placeholder="Tell us about your learning or teaching goals..."
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-bold text-text-main">Would you like to test the MVP?</label>
          <div className="flex gap-4">
            {['Yes', 'No'].map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setWantsToTestMvp(option as 'Yes' | 'No')}
                className={`flex-1 py-3 rounded-xl text-sm font-bold border transition-all ${
                  wantsToTestMvp === option 
                    ? 'bg-primary text-white border-primary' 
                    : 'bg-bg-main text-text-alt border-border-main hover:border-primary/50'
                }`}
              >
                {option}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <p className="text-sm text-red-500 font-medium">{error}</p>
        )}

        <button
          disabled={isSubmitting}
          type="submit"
          className="w-full py-4 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-2 shadow-xl shadow-indigo-500/20 active:scale-95 disabled:opacity-70"
        >
          {isSubmitting ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <>Join the Early Access List <Send className="w-5 h-5" /></>
          )}
        </button>
      </form>
    </div>
  );
}
